import React, { useMemo, type ReactNode } from "react";
import { Icon, Placeholder } from "semantic-ui-react";
import CroppedThumbnail from "../../croppedThumbnail/CroppedThumbnail";
import cn from "classnames";

import "./removableCard.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";

export interface RemovableCardProps {
  id: number;
  onRemoveItemClick?: (id: number, type?: string) => void;
  type?: string;
  title?: string;
  thumbnailUrl?: string | ReactNode;
  meta?: ReactNode;
  isReadOnly?: boolean;
  hidden?: boolean;
  className?: string;
  publisher?: string;
}

const RemovableCard: React.FC<RemovableCardProps> = (props) => {
  const handleRemoveIconClick = () => {
    props.onRemoveItemClick?.(props.id, props.type);
  };

  const thumbnail = useMemo(() => {
    if (!props.thumbnailUrl) {
      return (
        <Placeholder data-testid="loading-card-thumbnail">
          <Placeholder.Image />
        </Placeholder>
      );
    } else if (typeof props.thumbnailUrl === "string") {
      return (
        <CroppedThumbnail
          thumbnailUrl={props.thumbnailUrl}
          altText="removable card thumbnail"
          isHidden={props.hidden}
        />
      );
    } else {
      const icon = !props.hidden ? props.thumbnailUrl : <FontAwesomeIcon icon={faEyeSlash} />;
      return <div className={cn("removable-card-icon-background", { hiddenCardOverlay: !!props.hidden })}>{icon}</div>;
    }
  }, [props.thumbnailUrl, props.hidden]);

  return (
    <div key={props.id} className={cn("removable-card", props.className)}>
      <div className="image">{thumbnail}</div>
      <div className="content">
        {props.publisher && (
          <div className="vendor" data-testid="removable-card-vendor">
            {props.publisher}
          </div>
        )}
        <div className="title">
          {props.title || props.title === "" ? (
            <span data-testid="removable-card-title">{props.title}</span>
          ) : (
            <Placeholder data-testid="loading-card-data">
              <Placeholder.Header>
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Header>
            </Placeholder>
          )}
        </div>
        <div className="meta-text">{props.meta}</div>
      </div>
      {props.onRemoveItemClick && !props.isReadOnly && (
        <Icon
          fitted
          name="close"
          link
          onClick={handleRemoveIconClick}
          corner
          top="true"
          right="true"
          aria-label="remove-item"
          data-testid="remove-item"
        />
      )}
    </div>
  );
};

export default RemovableCard;
